import { useMemo, useState } from 'react';
import { Tree, Typography, Empty, Tag, Space, List, Card, Button, Alert, Modal, message } from 'antd';
import {
  FolderOpenOutlined, NumberOutlined, BookOutlined, FileTextOutlined,
  ContainerOutlined, UnorderedListOutlined, BarChartOutlined, ToolOutlined,
  ProfileOutlined, CalculatorOutlined, FundOutlined, TableOutlined,
  DatabaseOutlined, AppstoreOutlined, ApartmentOutlined, ScheduleOutlined,
  CloudOutlined, LinkOutlined, AreaChartOutlined, DeleteOutlined, WarningOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDocumentTypes, useCatalogTypes } from '@hooks/useMetadata';
import { useAllChartOfAccounts } from '@hooks/useAccounting';
import { useSubsystemTree } from '@hooks/useSubsystems';
import { enumsApi } from '@api/enumsApi';
import httpClient from '@api/httpClient';
import DocumentTypesPage from './DocumentTypesPage';
import CatalogTypesPage from './CatalogTypesPage';
import DynamicCatalogItemsPage from './DynamicCatalogItemsPage';
import DynamicDocumentsPage from '@modules/metadata/DynamicDocumentsPage';
import ChartOfAccountsPage from '@modules/accounting/ChartOfAccountsPage';
import SubsystemConstructorPage from '@modules/admin/SubsystemConstructorPage';
import JournalEntriesPage from '@modules/accounting/JournalEntriesPage';
import TrialBalancePage from '@modules/accounting/TrialBalancePage';

const { Title, Text } = Typography;

interface ConfigurationPageProps {
  /** Делегує навігацію до інших route-key (наприклад acc-counterparties). */
  acqOnNavigate: (acqRouteKey: string) => void;
}

/**
 * Конфігуратор-style сторінка: ліворуч ієрархічне дерево розділів конфігурації
 * (як у BAS Конфігураторі), праворуч — контент вибраного розділу.
 *
 * Активні розділи делегують до існуючих сторінок (DocumentTypesPage, ChartOfAccountsPage,
 * SubsystemConstructorPage, JournalEntriesPage). Розділи, які поки не реалізовано
 * (Константи, Регістри відомостей/накопичення, Бізнес-процеси, тощо) — Empty з тегом «Не реалізовано».
 */
export default function ConfigurationPage({ acqOnNavigate }: ConfigurationPageProps) {
  const [acqSelected, acqSetSelected] = useState<string>('overview');
  // Відкритий typeCode для DynamicCatalogItemsPage. Якщо null — показуємо CatalogTypesPage.
  const [acqOpenedCatalogCode, acqSetOpenedCatalogCode] = useState<string | null>(null);
  // Відкритий typeCode для DynamicDocumentsPage. Якщо null — показуємо DocumentTypesPage.
  const [acqOpenedDocCode, acqSetOpenedDocCode] = useState<string | null>(null);

  const acqDocTypesQuery = useDocumentTypes();
  const acqCatalogTypesQuery = useCatalogTypes();
  const acqChartQuery = useAllChartOfAccounts();
  const acqSubsystemsQuery = useSubsystemTree(true);
  const acqEnumsQuery = useQuery({
    queryKey: ['meta-enums', 'list'],
    queryFn: () => enumsApi.listEnums(),
  });

  const acqCounts = {
    documents: acqDocTypesQuery.data?.length ?? 0,
    chart: acqChartQuery.data?.length ?? 0,
    subsystems: acqSubsystemsQuery.data?.length ?? 0,
    enums: acqEnumsQuery.data?.length ?? 0,
    // Кількість довідників = реальні MetaCatalogType записи з БД (Kind=catalog).
    // Sprint 20+ Refactor Phase 7d: прибрано catalogsSystem з hardcoded REFERENCE_TYPES.
    catalogsTotal: acqCatalogTypesQuery.data?.length ?? 0,
    reports: 2, // ОСВ + Журнал проводок
  };

  const acqTreeData = useMemo(() => buildTree(acqCounts), [acqCounts]);

  const acqRenderContent = () => {
    switch (acqSelected) {
      case 'overview':
        return <OverviewPanel acqCounts={acqCounts} acqOnSelect={acqSetSelected} />;
      case 'catalogs':
        if (acqOpenedCatalogCode) {
          return <DynamicCatalogItemsPage typeCode={acqOpenedCatalogCode} onBack={() => acqSetOpenedCatalogCode(null)} />;
        }
        return <CatalogsPanel
          acqOnOpenItems={(c) => acqSetOpenedCatalogCode(c)}
        />;
      case 'documents':
        if (acqOpenedDocCode) {
          return <DynamicDocumentsPage typeCode={acqOpenedDocCode} onBack={() => acqSetOpenedDocCode(null)} />;
        }
        return <DocumentTypesPage acqOnOpenItems={(c) => acqSetOpenedDocCode(c)} />;
      case 'enums':
        return <EnumsPanel />;
      case 'reports':
        return <ReportsPanel acqOnNavigate={acqOnNavigate} />;
      case 'chart-of-accounts':
        return <ChartOfAccountsPage />;
      case 'accounting-registers':
        return <JournalEntriesPage />;
      case 'subsystems':
        return <SubsystemConstructorPage />;
      case 'reports-trial-balance':
        return <TrialBalancePage />;
      case 'reports-journal':
        return <JournalEntriesPage />;
      default:
        return <NotImplementedSection acqKey={acqSelected} />;
    }
  };

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 130px)', minHeight: 600 }}>
      {/* Ліве дерево розділів */}
      <div
        style={{
          width: 320,
          borderRight: '1px solid #f0f0f0',
          background: '#fafafa',
          padding: '12px 8px',
          overflowY: 'auto',
          flexShrink: 0,
        }}
      >
        <Title level={5} style={{ margin: '0 0 8px 8px' }}>Панель розробника</Title>
        <Tree
          showIcon
          blockNode
          defaultExpandAll
          selectedKeys={[acqSelected]}
          onSelect={(acqKeys) => acqKeys[0] && acqSetSelected(String(acqKeys[0]))}
          treeData={acqTreeData}
        />
      </div>

      {/* Правий контент */}
      <div style={{ flex: 1, padding: 16, overflowY: 'auto', minWidth: 0 }}>
        {acqRenderContent()}
      </div>
    </div>
  );
}

function buildTree(c: { documents: number; chart: number; subsystems: number; enums: number; catalogsTotal: number; reports: number }) {
  const acqDisabled = (acqTitle: string) => (
    <Space size={6}>
      <Text type="secondary">{acqTitle}</Text>
      <Tag color="default" style={{ fontSize: 10, lineHeight: '14px', padding: '0 4px' }}>не реалізовано</Tag>
    </Space>
  );
  const acqWithCount = (acqTitle: string, acqCount: number) => (
    <Space size={6}>
      <span>{acqTitle}</span>
      {acqCount > 0 && <Text type="secondary" style={{ fontSize: 11 }}>({acqCount})</Text>}
    </Space>
  );

  return [
    { key: 'overview', icon: <FolderOpenOutlined />, title: 'Огляд' },
    { key: '__general', icon: <FolderOpenOutlined />, title: acqDisabled('Загальні'), disabled: true, selectable: false },
    { key: '__constants', icon: <NumberOutlined />, title: acqDisabled('Константи'), disabled: true, selectable: false },
    { key: 'catalogs', icon: <BookOutlined />, title: acqWithCount('Довідники', c.catalogsTotal) },
    { key: 'documents', icon: <FileTextOutlined />, title: acqWithCount('Документи', c.documents) },
    { key: '__doc-journals', icon: <ContainerOutlined />, title: acqDisabled('Журнали документів'), disabled: true, selectable: false },
    { key: 'enums', icon: <UnorderedListOutlined />, title: acqWithCount('Перерахування', c.enums) },
    { key: 'reports', icon: <BarChartOutlined />, title: acqWithCount('Звіти', c.reports) },
    { key: '__data-processors', icon: <ToolOutlined />, title: acqDisabled('Обробки'), disabled: true, selectable: false },
    { key: '__characteristic-types', icon: <ProfileOutlined />, title: acqDisabled('Плани видів характеристик'), disabled: true, selectable: false },
    { key: 'chart-of-accounts', icon: <CalculatorOutlined />, title: acqWithCount('Плани рахунків', c.chart) },
    { key: '__calculation-types', icon: <FundOutlined />, title: acqDisabled('Плани видів розрахунку'), disabled: true, selectable: false },
    { key: '__info-registers', icon: <TableOutlined />, title: acqDisabled('Регістри відомостей'), disabled: true, selectable: false },
    { key: '__accumulation-registers', icon: <AreaChartOutlined />, title: acqDisabled('Регістри накопичення'), disabled: true, selectable: false },
    { key: 'accounting-registers', icon: <DatabaseOutlined />, title: acqWithCount('Регістри бухгалтерії', 1) },
    { key: '__calculation-registers', icon: <DatabaseOutlined />, title: acqDisabled('Регістри розрахунку'), disabled: true, selectable: false },
    { key: 'subsystems', icon: <AppstoreOutlined />, title: acqWithCount('Підсистеми', c.subsystems) },
    { key: '__business-processes', icon: <ApartmentOutlined />, title: acqDisabled('Бізнес-процеси'), disabled: true, selectable: false },
    { key: '__tasks', icon: <ScheduleOutlined />, title: acqDisabled('Задачі'), disabled: true, selectable: false },
    { key: '__external-data-sources', icon: <CloudOutlined />, title: acqDisabled('Зовнішні джерела даних'), disabled: true, selectable: false },
  ];
}

function OverviewPanel({ acqCounts, acqOnSelect }: {
  acqCounts: Record<string, number>;
  acqOnSelect: (k: string) => void;
}) {
  const acqCards: Array<{ key: string; title: string; count: number; icon: JSX.Element }> = [
    { key: 'catalogs', title: 'Довідники', count: acqCounts.catalogsTotal, icon: <BookOutlined /> },
    { key: 'documents', title: 'Документи', count: acqCounts.documents, icon: <FileTextOutlined /> },
    { key: 'enums', title: 'Перерахування', count: acqCounts.enums, icon: <UnorderedListOutlined /> },
    { key: 'reports', title: 'Звіти', count: acqCounts.reports, icon: <BarChartOutlined /> },
    { key: 'chart-of-accounts', title: 'Плани рахунків', count: acqCounts.chart, icon: <CalculatorOutlined /> },
    { key: 'accounting-registers', title: 'Регістри бухгалтерії', count: 1, icon: <DatabaseOutlined /> },
    { key: 'subsystems', title: 'Підсистеми', count: acqCounts.subsystems, icon: <AppstoreOutlined /> },
  ];
  return (
    <>
      <Title level={4} style={{ marginTop: 0 }}>Панель розробника</Title>
      <Text type="secondary">Активні розділи метаданих. Натисни на картку або на гілку дерева ліворуч.</Text>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginTop: 16 }}>
        {acqCards.map((c) => (
          <Card
            key={c.key}
            hoverable
            onClick={() => acqOnSelect(c.key)}
            styles={{ body: { padding: 14 } }}
          >
            <Space>
              <span style={{ fontSize: 22, color: '#1677ff' }}>{c.icon}</span>
              <div>
                <Text strong style={{ fontSize: 13 }}>{c.title}</Text>
                <div><Text type="secondary" style={{ fontSize: 12 }}>{c.count} об'єкт{c.count === 1 ? '' : c.count < 5 ? 'и' : 'ів'}</Text></div>
              </div>
            </Space>
          </Card>
        ))}
      </div>
      <DangerZone />
    </>
  );
}

function DangerZone() {
  const [acqOpen, acqSetOpen] = useState(false);
  const [acqLoading, acqSetLoading] = useState(false);
  const acqQc = useQueryClient();

  const acqHandleReset = async () => {
    acqSetLoading(true);
    try {
      const acqRes = await httpClient.delete<{ rowsBefore: Record<string, number>; elapsedMs: number }>('/admin/database/reset-metadata');
      const acqTotal = Object.values(acqRes.data.rowsBefore).reduce((s, n) => s + n, 0);
      message.success(`Очищено ${acqTotal} записів за ${acqRes.data.elapsedMs}мс. Сторінка перезавантажиться.`);
      acqQc.invalidateQueries();
      // Перезавантажуємо щоб counts/subsystems оновились + browser cache не плутав.
      setTimeout(() => window.location.reload(), 1000);
    } catch (e: unknown) {
      message.error(`Помилка очищення: ${(e as Error).message}`);
    } finally {
      acqSetLoading(false);
      acqSetOpen(false);
    }
  };

  return (
    <>
      <Alert
        type="error"
        showIcon
        icon={<WarningOutlined />}
        style={{ marginTop: 24 }}
        message="Зона небезпеки"
        description={
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              Видалити всі метадані (типи документів/довідників, реквізити, перерахування, підсистеми)
              + динамічні дані (документи, записи довідників, проводки, BAS-мапінги).
              Зберігаються: користувачі, ролі, audit log, План рахунків + базові валюти + ставки ПДВ
              (re-seed на старті API).
            </Text>
            <Button danger icon={<DeleteOutlined />} onClick={() => acqSetOpen(true)}>
              Очистити всі метадані та дані
            </Button>
          </Space>
        }
      />
      <Modal
        title={<Space><WarningOutlined style={{ color: '#ff4d4f' }} /> Підтвердження повного скидання</Space>}
        open={acqOpen}
        onOk={acqHandleReset}
        onCancel={() => acqSetOpen(false)}
        okButtonProps={{ danger: true, loading: acqLoading }}
        okText="Так, очистити"
        cancelText="Скасувати"
      >
        <Text>
          Цю дію <Text strong>не можна скасувати</Text>. Усі типи, реквізити, документи,
          записи довідників і проводки буде видалено. Продовжити?
        </Text>
      </Modal>
    </>
  );
}

function CatalogsPanel({ acqOnOpenItems }: {
  acqOnOpenItems: (typeCode: string) => void;
}) {
  // Sprint 20+ Refactor Етап 7: усі довідники тепер уніфіковані meta-каталоги.
  // Окремий tab "Системні (Domain)" видалено — концепт мертвий після Phase 7a
  // (видалено 23 типізованих Domain controllers + MediatR layer). IsSystem=true
  // лише позначає snapshot системних довідників, які не можна видалити з UI.
  return <CatalogTypesPage acqOnOpenItems={acqOnOpenItems} />;
}

function EnumsPanel() {
  const acqQuery = useQuery({ queryKey: ['meta-enums', 'list'], queryFn: () => enumsApi.listEnums() });
  return (
    <>
      <Title level={4} style={{ marginTop: 0 }}>Перерахування ({acqQuery.data?.length ?? 0})</Title>
      <Text type="secondary">BAS-перерахування (`meta_enums`). Кожен використовується через EnumPicker у формах документів.</Text>
      <List
        style={{ marginTop: 12 }}
        loading={acqQuery.isLoading}
        dataSource={acqQuery.data ?? []}
        bordered
        size="small"
        locale={{ emptyText: 'Немає перерахувань. Імпортуй BAS Phase 0c.' }}
        renderItem={(e) => (
          <List.Item>
            <Space>
              <UnorderedListOutlined />
              <Text strong>{e.uaName || e.basName}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>{e.basName}</Text>
              <Tag>{e.values?.length ?? 0} значень</Tag>
            </Space>
          </List.Item>
        )}
      />
    </>
  );
}

function ReportsPanel({ acqOnNavigate }: { acqOnNavigate: (k: string) => void }) {
  const acqReports = [
    { key: 'rpt-trial-balance', title: 'Оборотно-сальдова відомість', desc: 'Сальдо початок/кінець, обороти за період, фільтри по рахунку та організації.' },
    { key: 'rpt-journal', title: 'Журнал проводок', desc: 'Усі записи регістру «Хозрасчетний» з фільтрами по даті, документу, рахунках.' },
  ];
  return (
    <>
      <Title level={4} style={{ marginTop: 0 }}>Звіти ({acqReports.length})</Title>
      <List
        style={{ marginTop: 12 }}
        dataSource={acqReports}
        bordered
        renderItem={(r) => (
          <List.Item
            actions={[
              <Button type="link" size="small" onClick={() => acqOnNavigate(r.key)} icon={<LinkOutlined />}>Відкрити</Button>,
            ]}
          >
            <Space direction="vertical" size={0}>
              <Space><BarChartOutlined /><Text strong>{r.title}</Text></Space>
              <Text type="secondary" style={{ fontSize: 12 }}>{r.desc}</Text>
            </Space>
          </List.Item>
        )}
      />
    </>
  );
}

function NotImplementedSection({ acqKey }: { acqKey: string }) {
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <Space direction="vertical" size={4}>
          <Text>Розділ <Text code>{acqKey}</Text> ще не реалізовано.</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Цей пункт зарезервовано для відповідності BAS-моделі конфігурації. Реалізація буде у наступних спринтах.
          </Text>
        </Space>
      }
    />
  );
}
